import { useSelector } from 'react-redux';
import { Layout, Menu, Tooltip } from 'antd';
import { DashboardOutlined, UserOutlined, FileTextOutlined, SettingOutlined, SearchOutlined, PlusOutlined, SyncOutlined, CheckCircleOutlined, FileTextOutlined as LogoIcon, AuditOutlined, ToolOutlined, MonitorOutlined, FileSearchOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useSupplierProfile from '../../hooks/useSupplierProfile';

const { Sider } = Layout;

// Map page keys to routes
const pageRoutes = {
  'dashboard': '/dashboard',
  'all-invoices': '/invoices',
  'create-invoice': '/invoices/create',
  'pending-invoices': '/invoices/pending',
  'submitted-invoices': '/invoices/submitted',
  'settings': '/settings',
  'users': '/users',
  'audit-logs': '/audit',
  'maintenance-mode': '/admin/maintenance',
  'invoice-monitoring': '/admin/invoice-monitoring',
  'system-health': '/admin/system-health',
};

const Sidebar = ({ collapsed, setCollapsed, currentPage, setCurrentPage, isMobile }) => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user && user.role === 'admin';
  const navigate = useNavigate();
  const { isComplete: isProfileComplete, percentage: profilePercentage } = useSupplierProfile();

  const handleMenuClick = ({ key }) => {
    // Don't navigate if profile is incomplete and trying to access invoice features
    if (!isProfileComplete && (key === 'create-invoice' || key === 'pending-invoices' || key === 'all-invoices' || key === 'submitted-invoices')) {
      return;
    }
    
    // Navigate to the corresponding route (single source of navigation).
    // Do NOT also call setCurrentPage here: it navigates too (AppLayout.handlePageChange),
    // and a double navigate freezes React Router v7 when CreateInvoice's useBlocker is mounted.
    const route = pageRoutes[key];
    if (route) {
      navigate(route);
    }
    
    // Close sidebar on mobile after menu click (only on true mobile, not tablet)
    if (isMobile) {
      setCollapsed(true);
    }
  };

  let menuItems;

  // Helper function to create menu item with link support
  const createMenuItem = (key, icon, label, disabled = false, tooltipText = null) => {
    const item = {
      key,
      icon,
      disabled,
      label: disabled && tooltipText ? (
        <Tooltip title={tooltipText} placement="right">
          <span style={{ 
            opacity: 0.5, 
            cursor: 'not-allowed',
            display: 'flex',
            alignItems: 'center'
          }}>
            {label}
            <LockOutlined style={{ fontSize: '12px', marginLeft: '8px' }} />
          </span>
        </Tooltip>
      ) : (
        <a 
          href={pageRoutes[key]} 
          onClick={(e) => {
            // Only prevent default for left click (button 0)
            // Allow right-click to show browser context menu
            if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
            }
          }}
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          {label}
        </a>
      )
    };
    return item;
  };

  // Admin users see admin-related menu items with logical grouping
  if (isAdmin) {
    menuItems = [
      // Operations Group
      {
        type: 'group',
        label: 'OPERATIONS',
        children: [
          createMenuItem('invoice-monitoring', <FileSearchOutlined />, 'Invoice Monitoring'),
          createMenuItem('users', <UserOutlined />, 'Users'),
          createMenuItem('audit-logs', <AuditOutlined />, 'Audit Logs'),
        ]
      },
      // System Group
      {
        type: 'group',
        label: 'SYSTEM',
        children: [
          createMenuItem('system-health', <MonitorOutlined />, 'System Health'),
          createMenuItem('maintenance-mode', <ToolOutlined />, 'Maintenance Mode'),
        ]
      },
    ];
  } else {
    // Non-admin users see the standard menu
    // Disable invoice-related items if profile is incomplete
    const profileTooltip = `Complete your supplier profile first (${profilePercentage}% done)`;
    
    menuItems = [
      createMenuItem('dashboard', <DashboardOutlined />, 'Home'),
      createMenuItem('all-invoices', <SearchOutlined />, 'All Invoices', !isProfileComplete, profileTooltip),
      createMenuItem('create-invoice', <PlusOutlined />, 'Create Invoice', !isProfileComplete, profileTooltip),
      createMenuItem('pending-invoices', <SyncOutlined />, 'Pending Invoices', !isProfileComplete, profileTooltip),
      createMenuItem('submitted-invoices', <CheckCircleOutlined />, 'Submitted Invoices', !isProfileComplete, profileTooltip),
      createMenuItem('settings', <SettingOutlined />, 'Settings')
    ];
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={208}
      className={`ant-layout-sider ${isMobile && !collapsed ? 'mobile-sidebar-open' : ''}`}
    >
      <div className="logo-container">
        <div className="logo">
          <LogoIcon style={{ fontSize: '18px', color: '#fff' }} />
        </div>
        {!collapsed && <span className="logo-text">GIS E-Invoice</span>}
      </div>

      <Menu
        theme="dark"
        selectedKeys={[currentPage]}
        mode="inline"
        onClick={handleMenuClick}
        className="sidebar-menu"
        items={menuItems}
      />
    </Sider>
  );
};

export default Sidebar;
